import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import {
    FaArrowLeft, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaCalendarAlt,
    FaClock, FaDollarSign, FaCouch, FaWifi, FaBath, FaCar, FaUsers,
    FaSmoking, FaSmokingBan, FaPaw, FaChild, FaRainbow, FaVenusMars,
    FaBriefcase, FaCheck, FaCamera, FaInfoCircle, FaIdCard,
    FaUserFriends, FaHome, FaExclamationTriangle, FaBed
} from 'react-icons/fa';
import './FlatmateProfileView.css';
import { getFullImageUrl } from '../utils/url';

/* ---------------- Safe data helpers ---------------- */

const cleanText = (val) => {
    if (val === null || val === undefined) return '';
    const str = typeof val === 'object' ? '' : String(val).trim();
    return str === 'null' || str === 'undefined' ? '' : str;
};

const asArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
        const trimmed = val.trim();
        if (!trimmed) return [];
        try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed : [trimmed];
        } catch (e) {
            return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
        }
    }
    return [];
};

const asObject = (val) => {
    if (!val) return {};
    if (typeof val === 'object' && !Array.isArray(val)) return val;
    if (typeof val === 'string') {
        try {
            const parsed = JSON.parse(val);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (e) {
            return {};
        }
    }
    return {};
};

/* Only plain scalar values survive (no [object Object], no empty chips) */
const cleanList = (val) => asArray(val)
    .filter((v) => v !== null && v !== undefined && typeof v !== 'object')
    .map((v) => String(v).trim())
    .filter(Boolean);

const uniq = (arr) => [...new Set(arr)];

const getImgUrl = (img) => {
    const src = cleanText(img);
    return getFullImageUrl(src);
};

const fmtDate = (val) => {
    const str = cleanText(val);
    if (!str) return '—';
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

function FlatmateProfileView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const fetchProfile = async () => {
            try {
                const res = await axiosInstance.get(`/flatmates/${id}`);
                if (mounted) {
                    setProfile(res.data || null);
                    setNotFound(!res.data);
                }
            } catch (error) {
                console.error('Error fetching flatmate profile:', error);
                if (mounted) {
                    setNotFound(true);
                    toast.error(error.response?.status === 404 ? 'Flatmate profile not found' : 'Failed to load profile details');
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchProfile();
        return () => { mounted = false; };
    }, [id]);

    /* ---------------- Loading state ---------------- */
    if (loading) {
        return (
            <div className="page-premium-wrapper flatmate-profile-page">
                <div className="page-premium-content">
                    <div className="fp-center-state">
                        <div className="fp-spinner"></div>
                        <span style={{ fontSize: '0.9rem' }}>Loading profile details…</span>
                    </div>
                </div>
            </div>
        );
    }

    /* ---------------- Not found state ---------------- */
    if (notFound || !profile) {
        return (
            <div className="page-premium-wrapper flatmate-profile-page">
                <div className="page-premium-content">
                    <div className="fp-center-state">
                        <div className="fp-error-icon"><FaExclamationTriangle /></div>
                        <h4 style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>Flatmate profile not found</h4>
                        <p style={{ margin: 0, fontSize: '0.88rem' }}>It may have been deleted or the link is incorrect.</p>
                        <button className="fp-back-btn" onClick={() => navigate('/flatmates')}>
                            <FaArrowLeft /> Back to Flatmates
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /* ---------------- Derived, cleaned data ---------------- */
    const user = profile.User || {};
    const fullName = cleanText(profile.fullName) || cleanText(user.name) || 'Roommate Seeker';
    const avatarUrl = getImgUrl(profile.profilePicture || user.avatar);
    const initial = fullName.charAt(0).toUpperCase() || 'U';

    const suburbs = uniq(cleanList(profile.suburbs));
    const placeTypes = uniq(cleanList(profile.place_types));
    const city = cleanText(profile.city);
    const state = cleanText(profile.state);
    const locationLine = [city, state].filter(Boolean).join(', ');

    const bio = cleanText(profile.bio); // ONLY the actual user bio

    const prefs = asObject(profile.property_preferences);
    const prefItems = [
        { icon: <FaCouch />, label: 'Furnishing', value: cleanText(prefs.furnishing) },
        { icon: <FaWifi />, label: 'Internet', value: cleanText(prefs.internet) },
        { icon: <FaBath />, label: 'Bathroom', value: cleanText(prefs.bathroom) },
        { icon: <FaCar />, label: 'Parking', value: cleanText(prefs.parking) },
        { icon: <FaUsers />, label: 'Max Flatmates', value: cleanText(prefs.maxFlatmates) },
    ];
    const prefExtra = uniq(cleanList(prefs.preferences_list));
    const hasAnyPref = prefItems.some((p) => p.value) || prefExtra.length > 0;

    const life = asObject(profile.lifestyle);
    const lifeTags = uniq(cleanList(life.tags));
    const lifeFlags = [];
    if (life.smoker !== undefined) {
        lifeFlags.push(life.smoker
            ? { key: 'smoker', text: 'Smoker', tone: 'amber', icon: <FaSmoking /> }
            : { key: 'smoker', text: 'Non-smoker', tone: 'green', icon: <FaSmokingBan /> });
    }
    if (life.pets !== undefined) {
        lifeFlags.push(life.pets
            ? { key: 'pets', text: 'Pet Friendly', tone: 'green', icon: <FaPaw /> }
            : { key: 'pets', text: 'No Pets', tone: 'gray', icon: <FaPaw /> });
    }
    if (life.children !== undefined) {
        lifeFlags.push(life.children
            ? { key: 'children', text: 'Children OK', tone: 'green', icon: <FaChild /> }
            : { key: 'children', text: 'No Children', tone: 'gray', icon: <FaChild /> });
    }
    if (life.lgbtqia) {
        lifeFlags.push({ key: 'lgbtqia', text: 'LGBTQIA+ Friendly', tone: 'purple', icon: <FaRainbow /> });
    }
    const hasLifestyle = lifeFlags.length > 0 || lifeTags.length > 0;

    const about = asObject(profile.about_you);
    const seekerType = cleanText(about.seekerType) || 'Me';
    const seekerAge = cleanText(about.age);
    const seekerGender = cleanText(about.gender);
    const aboutList = uniq(cleanList(about.about_list));
    const couple = asObject(about.coupleDetails);
    const group = asObject(about.groupDetails);

    const employmentTags = uniq([
        ...cleanList(profile.employment_statuses),
        ...cleanList([profile.employment_status]),
        ...cleanList([profile.occupation_type]),
    ]);

    const photos = uniq(cleanList(profile.photos).map(getImgUrl).filter(Boolean));
    const budget = cleanText(profile.rent_budget);
    const moveDate = cleanText(profile.move_date);
    const stayLength = cleanText(profile.stay_length);
    const genderPref = cleanText(profile.looking_for_gender) || 'Any';
    const isActive = !!profile.is_active;
    const teamups = !!profile.teamups;

    return (
        <div className="page-premium-wrapper flatmate-profile-page">
            <div className="page-premium-content">

                {/* ---------- Hero Header ---------- */}
                <div className="page-hero-header">
                    <div className="fp-hero">
                        <div className="fp-hero-left">
                            <div className="fp-hero-avatar">
                                {avatarUrl ? <img src={avatarUrl} alt={fullName} /> : initial}
                            </div>
                            <div>
                                <h1 className="ph-title">{fullName}</h1>
                                <p className="ph-subtitle">
                                    {locationLine || 'Location not set'}
                                    {' · '}
                                    {seekerType === 'Me' ? 'Individual Seeker' : `${seekerType} Seeker`}
                                    {employmentTags.length > 0 ? ` · ${employmentTags[0]}` : ''}
                                </p>
                            </div>
                        </div>
                        <div className="fp-hero-right">
                            <span className={`fp-status ${isActive ? 'fp-status-active' : 'fp-status-inactive'}`}>
                                <span className="fp-dot"></span>
                                {isActive ? 'Active Profile' : 'Inactive / Suspended'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ---------- Back button ---------- */}
                <div style={{ marginBottom: '18px' }}>
                    <button className="fp-back-btn" onClick={() => navigate('/flatmates')}>
                        <FaArrowLeft /> Back to Flatmates
                    </button>
                </div>

                {/* ---------- Content grid ---------- */}
                <div className="fp-grid">
                    <div className="fp-col-main">

                        {/* Bio */}
                        <div className="fp-card">
                            <h3 className="fp-card-title"><span className="fp-title-bar"></span> About / Bio</h3>
                            {bio
                                ? <p className="fp-bio">{bio}</p>
                                : <p className="fp-empty-note">No bio provided yet.</p>}
                        </div>

                        {/* Looking For */}
                        <div className="fp-card">
                            <h3 className="fp-card-title">
                                <span className="fp-title-bar"></span>
                                <FaHome className="fp-title-icon" /> Looking For
                            </h3>

                            <div className="fp-chips" style={{ marginBottom: '14px' }}>
                                {placeTypes.length > 0
                                    ? placeTypes.map((pt) => (
                                        <span key={pt} className="fp-chip fp-chip-blue">
                                            <FaBed className="fp-chip-icon" /> {pt}
                                        </span>
                                    ))
                                    : <span className="fp-empty-note" style={{ width: '100%' }}>No specific place type selected.</span>}
                            </div>

                            <div className="fp-chips" style={{ marginBottom: '14px' }}>
                                {suburbs.length > 0
                                    ? suburbs.map((s) => (
                                        <span key={s} className="fp-chip fp-chip-red">
                                            <FaMapMarkerAlt className="fp-chip-icon" /> {s}
                                        </span>
                                    ))
                                    : <span className="fp-empty-note" style={{ width: '100%' }}>No preferred suburbs listed.</span>}
                            </div>

                            <div className="fp-pref-grid">
                                <div className="fp-pref-item">
                                    <span className="fp-pref-label"><span className="fp-pref-icon"><FaDollarSign /></span>Budget</span>
                                    <span className={`fp-pref-value ${budget ? 'is-set' : 'is-flexible'}`}>
                                        {budget ? `$${budget} / wk` : 'Flexible'}
                                    </span>
                                </div>
                                <div className="fp-pref-item">
                                    <span className="fp-pref-label"><span className="fp-pref-icon"><FaCalendarAlt /></span>Move Date</span>
                                    <span className={`fp-pref-value ${moveDate ? 'is-set' : 'is-flexible'}`}>
                                        {moveDate ? fmtDate(moveDate) : 'Flexible'}
                                    </span>
                                </div>
                                <div className="fp-pref-item">
                                    <span className="fp-pref-label"><span className="fp-pref-icon"><FaClock /></span>Stay Length</span>
                                    <span className={`fp-pref-value ${stayLength ? 'is-set' : 'is-flexible'}`}>
                                        {stayLength || 'Flexible'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Property Preferences */}
                        <div className="fp-card">
                            <h3 className="fp-card-title">
                                <span className="fp-title-bar"></span>
                                <FaBed className="fp-title-icon" /> Property Preferences
                            </h3>
                            {hasAnyPref ? (
                                prefItems.some((p) => p.value) ? (
                                    <div className="fp-pref-grid">
                                        {prefItems.filter((p) => p.value).map((p) => (
                                            <div key={p.label} className="fp-pref-item">
                                                <span className="fp-pref-label">
                                                    <span className="fp-pref-icon">{p.icon}</span>{p.label}
                                                </span>
                                                <span className={`fp-pref-value ${p.value.toLowerCase() === 'flexible' ? 'is-flexible' : 'is-set'}`}>
                                                    {p.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="fp-chips">
                                        {prefExtra.map((p) => (
                                            <span key={p} className="fp-chip fp-chip-gray">
                                                <FaCheck className="fp-chip-icon" /> {p}
                                            </span>
                                        ))}
                                    </div>
                                )
                            ) : (
                                <p className="fp-empty-note">No property preferences set.</p>
                            )}
                        </div>

                        {/* About the Seeker */}
                        <div className="fp-card">
                            <h3 className="fp-card-title">
                                <span className="fp-title-bar"></span>
                                <FaUserFriends className="fp-title-icon" /> About the Seeker
                            </h3>

                            <div className="fp-chips" style={{ marginBottom: '12px' }}>
                                <span className="fp-chip fp-chip-purple">
                                    <FaUserFriends className="fp-chip-icon" />
                                    {seekerType === 'Me' ? 'Individual Seeker' : `${seekerType} Seeker`}
                                </span>
                                {seekerAge && (
                                    <span className="fp-chip fp-chip-blue"><FaIdCard className="fp-chip-icon" /> {seekerAge} yrs</span>
                                )}
                                {seekerGender && (
                                    <span className="fp-chip fp-chip-blue"><FaVenusMars className="fp-chip-icon" /> {seekerGender}</span>
                                )}
                            </div>

                            {seekerType === 'Couple' && (cleanText(couple.p1Name) || cleanText(couple.p2Name)) && (
                                <p className="fp-bio" style={{ fontSize: '0.86rem', marginBottom: '12px' }}>
                                    Couple: {cleanText(couple.p1Name) || 'Partner 1'} &amp; {cleanText(couple.p2Name) || 'Partner 2'}
                                </p>
                            )}
                            {seekerType === 'Group' && cleanText(group.groupSize) && (
                                <p className="fp-bio" style={{ fontSize: '0.86rem', marginBottom: '12px' }}>
                                    Group of {cleanText(group.groupSize)} seekers
                                </p>
                            )}

                            {aboutList.length > 0 && (
                                <div className="fp-chips" style={{ marginBottom: employmentTags.length > 0 ? '14px' : '0' }}>
                                    {aboutList.map((t) => (
                                        <span key={t} className="fp-chip fp-chip-gray">{t}</span>
                                    ))}
                                </div>
                            )}

                            {employmentTags.length > 0 && (
                                <>
                                    <h4 className="fp-mini-title" style={{ marginBottom: '8px' }}>
                                        <FaBriefcase className="fp-mini-icon" /> Employment
                                    </h4>
                                    <div className="fp-chips">
                                        {employmentTags.map((t) => (
                                            <span key={t} className="fp-chip fp-chip-green">
                                                <FaBriefcase className="fp-chip-icon" /> {t}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Lifestyle */}
                        <div className="fp-card">
                            <h3 className="fp-card-title">
                                <span className="fp-title-bar"></span>
                                <FaInfoCircle className="fp-title-icon" /> Lifestyle
                            </h3>
                            {hasLifestyle ? (
                                <>
                                    {lifeFlags.length > 0 && (
                                        <div className="fp-chips" style={{ marginBottom: lifeTags.length > 0 ? '12px' : '0' }}>
                                            {lifeFlags.map((f) => (
                                                <span key={f.key} className={`fp-chip fp-chip-${f.tone}`}>
                                                    <span className="fp-chip-icon">{f.icon}</span> {f.text}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {lifeTags.length > 0 && (
                                        <div className="fp-chips">
                                            {lifeTags.map((t) => (
                                                <span key={t} className="fp-chip fp-chip-amber">{t}</span>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="fp-empty-note">No lifestyle details added.</p>
                            )}
                        </div>

                        {/* Photos */}
                        {photos.length > 0 && (
                            <div className="fp-card">
                                <h3 className="fp-card-title">
                                    <span className="fp-title-bar"></span>
                                    <FaCamera className="fp-title-icon" /> Photos ({photos.length})
                                </h3>
                                <div className="fp-photo-grid">
                                    {photos.map((src, i) => (
                                        <a key={src + i} className="fp-photo" href={src} target="_blank" rel="noopener noreferrer">
                                            <img src={src} alt={`Flatmate upload ${i + 1}`} loading="lazy" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ---------- Sidebar ---------- */}
                    <div className="fp-col-side">

                        {/* User card */}
                        <div className="fp-card fp-user-card">
                            <div className="fp-user-avatar">
                                {avatarUrl ? <img src={avatarUrl} alt={fullName} /> : initial}
                            </div>
                            <h3 className="fp-user-name">{fullName}</h3>
                            <p className="fp-user-role">Flatmate Seeker · Profile #{profile.id}</p>
                            <div className="fp-contact-list">
                                {cleanText(user.email) && (
                                    <div className="fp-contact-item">
                                        <FaEnvelope className="fp-contact-icon" /> {user.email}
                                    </div>
                                )}
                                {cleanText(user.phone) && (
                                    <div className="fp-contact-item">
                                        <FaPhoneAlt className="fp-contact-icon" /> {user.phone}
                                    </div>
                                )}
                                {locationLine && (
                                    <div className="fp-contact-item">
                                        <FaMapMarkerAlt className="fp-contact-icon" /> {locationLine}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick facts */}
                        <div className="fp-card">
                            <h3 className="fp-card-title"><span className="fp-title-bar"></span> Quick Facts</h3>
                            <div className="fp-facts">
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaDollarSign /></span> Budget</span>
                                    <span className="fp-fact-value text-strong" style={{ color: budget ? '#0e9f5f' : undefined }}>
                                        {budget ? `$${budget} / wk` : 'Flexible'}
                                    </span>
                                </div>
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaCalendarAlt /></span> Move Date</span>
                                    <span className="fp-fact-value">{moveDate ? fmtDate(moveDate) : 'Flexible'}</span>
                                </div>
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaClock /></span> Stay Length</span>
                                    <span className="fp-fact-value">{stayLength || 'Flexible'}</span>
                                </div>
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaVenusMars /></span> Gender Pref.</span>
                                    <span className="fp-fact-value">{genderPref}</span>
                                </div>
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaUsers /></span> Teamups</span>
                                    <span className={`fp-fact-value ${teamups ? 'text-strong' : ''}`} style={{ color: teamups ? '#0e9f5f' : undefined }}>
                                        {teamups ? 'Allowed' : 'Not Allowed'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* Account info */}
                        <div className="fp-card">
                            <h3 className="fp-card-title"><span className="fp-title-bar"></span> Account Info</h3>
                            <div className="fp-facts">
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaIdCard /></span> Profile ID</span>
                                    <span className="fp-fact-value">#{profile.id}</span>
                                </div>
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaUserFriends /></span> User ID</span>
                                    <span className="fp-fact-value">{(user.id || profile.user_id) ? `#${user.id || profile.user_id}` : '—'}</span>
                                </div>
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaCalendarAlt /></span> Created</span>
                                    <span className="fp-fact-value">{fmtDate(profile.createdAt)}</span>
                                </div>
                                <div className="fp-fact">
                                    <span className="fp-fact-label"><span className="fp-fact-icon"><FaClock /></span> Updated</span>
                                    <span className="fp-fact-value">{fmtDate(profile.updatedAt)}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default FlatmateProfileView;